import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Video as VideoIcon, MessageCircle } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a4b18afd-b735-452c-a88b-6aee2704ff09/images/f90fb771c72c62abc1b00beb82dd15d495288fe0213f233f4fec560cf0e2ca90.jpeg";

const PETAL_COLORS = ["#f59e0b", "#f43f5e", "#10b981", "#f3e5ab", "#fb923c"];

const petals = Array.from({ length: 18 }).map((_, i) => ({
  id: i,
  left: `${(i * 5.5 + (i % 3) * 4) % 100}%`,
  size: 6 + (i % 4) * 4,
  duration: 9 + (i % 6) * 2.5,
  delay: (i % 9) * 1.4,
  color: PETAL_COLORS[i % PETAL_COLORS.length],
  round: i % 2 === 0,
}));

const sparkles = Array.from({ length: 22 }).map((_, i) => ({
  id: i,
  top: `${(i * 37) % 95}%`,
  left: `${(i * 53) % 98}%`,
  size: 3 + (i % 3) * 2,
  duration: 2 + (i % 4),
  delay: (i % 7) * 0.6,
}));

export const FestiveHero = ({ whatsapp, total, categoriesCount, onExplore }) => {
  const chatNow = () =>
    window.open(
      `https://wa.me/${whatsapp}?text=${encodeURIComponent(
        "Hi Soulful Invites! I'd love to know more about your animated 3D invites."
      )}`,
      "_blank"
    );

  const stats = [
    { value: total > 0 ? `${total}+` : "500+", label: "Bespoke Designs" },
    { value: `${categoriesCount || 14}+`, label: "Event Categories" },
    { value: "All Faiths", label: "Every Tradition" },
  ];

  return (
    <section className="relative overflow-hidden bg-[#160a12]">
      {/* Animated base image */}
      <img
        src={HERO_IMG}
        alt="Festive celebration"
        className="absolute inset-0 w-full h-full object-cover opacity-60 animate-hue"
      />
      {/* Colour wash overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4a0e1a]/70 via-[#160a12]/60 to-[#0d3b2e]/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#120E0C] via-transparent to-[#120E0C]/40" />
      {/* Radial festive glows */}
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-amber-500/20 blur-3xl" />
      <div className="absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-rose-500/15 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-emerald-500/15 blur-3xl" />

      {/* Sparkles */}
      {sparkles.map((s) => (
        <span
          key={`sp-${s.id}`}
          className="sparkle absolute rounded-full bg-amber-200"
          style={{
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            boxShadow: "0 0 8px 2px rgba(243,229,171,0.7)",
            animation: `twinkle ${s.duration}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
      {/* Floating petals */}
      {petals.map((p) => (
        <span
          key={`pt-${p.id}`}
          className="petal absolute bottom-[-40px]"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: p.round ? "9999px" : "40% 60% 60% 40%",
            animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}

      {/* Spinning mandala rings */}
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border border-amber-400/10 spin-slow" />
      <div className="pointer-events-none absolute -right-28 -top-28 h-96 w-96 rounded-full border border-amber-400/10 spin-slow" style={{ animationDuration: "42s" }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase bg-amber-500/15 text-amber-200 border border-amber-400/30 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> Animated 3D E-Invitations
          </span>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-50 leading-[1.05] tracking-tight">
            Crafting Timeless Memories with{" "}
            <span className="festive-text">Luxury E-Invites</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-stone-200/90 leading-relaxed max-w-2xl">
            Discover personalised <span className="text-amber-300 font-medium">animated 3D video invitations</span> for
            Weddings, Anniversaries, Birthdays & treasured traditional ceremonies. Every faith, every
            tradition — brought to life with colour and motion.
          </p>

          <div className="mt-7 inline-flex items-center gap-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 backdrop-blur px-5 py-3">
            <Sparkles className="h-5 w-5 text-amber-300" />
            <span className="text-amber-50">
              <span className="font-serif text-2xl sm:text-3xl font-bold text-amber-300">₹250</span>
              <span className="text-sm sm:text-base font-medium"> + GST onwards</span>
            </span>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              data-testid="explore-catalogue-btn"
              onClick={onExplore}
              className="shimmer-btn inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 text-white font-semibold py-3.5 px-7 shadow-lg hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02]"
            >
              <VideoIcon className="h-5 w-5" /> Explore Catalogue
            </button>
            <button
              data-testid="hero-whatsapp-btn"
              onClick={chatNow}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 backdrop-blur border border-white/25 text-white font-medium py-3.5 px-6 hover:bg-white/20 transition-all"
            >
              <MessageCircle className="h-5 w-5 text-emerald-300" /> Chat with us
            </button>
          </div>

          <div className="mt-12 flex flex-wrap gap-8">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="font-serif text-2xl sm:text-3xl font-bold text-amber-300">{s.value}</div>
                <div className="text-xs tracking-widest uppercase text-stone-300/80 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
